/**
 * FilmCutColorSection — DTF film trim-line colour, per location.
 *
 * Each film starts with a solid trim line (above its header) the cutting
 * machine colour-detects and cuts along, separating the films on a sheet
 * from each other. This section sets that colour per location
 * (stored in `locations.production_settings.film_cut_color` as "#rrggbb")
 * via a native colour picker + a hex field kept in sync. Unset falls back to
 * the compositor default (#FF00FF magenta). Clear returns to the default.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Loader2,
  AlertTriangle,
  RefreshCw,
  RotateCcw,
  Scissors,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { trpc } from '@/lib/trpc';

/** The compositor's fallback when no colour is configured. */
const DEFAULT_CUT_COLOR = '#FF00FF';
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

interface LocationRow {
  id: string;
  name: string;
  filmCutColor: string | null;
}

interface FilmCutColorRowProps {
  location: LocationRow;
  onSave: (locationId: string, color: string | null) => Promise<void>;
  isSaving: boolean;
  /** False in Console single-location mode — the section header already names the location. */
  showName?: boolean;
}

function FilmCutColorRow({ location, onSave, isSaving, showName = true }: FilmCutColorRowProps) {
  const { t } = useTranslation('settings');
  const saved = location.filmCutColor;
  const [hex, setHex] = useState(saved ?? DEFAULT_CUT_COLOR);

  // Re-sync when the server value changes (save elsewhere / invalidate).
  useEffect(() => {
    setHex(saved ?? DEFAULT_CUT_COLOR);
  }, [saved, location.id]);

  const valid = HEX_RE.test(hex);
  const dirty = valid && hex.toLowerCase() !== (saved ?? '').toLowerCase();

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors"
      role="listitem"
    >
      <div className="flex items-center gap-2 min-w-0 sm:w-56">
        {showName && <span className="font-medium text-sm truncate">{location.name}</span>}
        {saved ? (
          <Badge variant="info" className="shrink-0">{t('production.filmCutColor.set')}</Badge>
        ) : (
          <Badge
            variant="outline"
            className="text-muted-foreground border-muted-foreground/30 font-normal shrink-0"
          >
            {t('production.filmCutColor.usingDefault')}
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {/* Native colour picker — kept in sync with the hex field. */}
        <input
          type="color"
          value={valid ? hex : DEFAULT_CUT_COLOR}
          onChange={(e) => setHex(e.target.value.toUpperCase())}
          className="h-10 w-14 cursor-pointer rounded-md border border-input bg-background p-1"
          aria-label={t('production.filmCutColor.pickerAria', { location: location.name })}
          disabled={isSaving}
        />
        <Input
          value={hex}
          onChange={(e) => {
            const v = e.target.value.trim();
            setHex(v.startsWith('#') || v === '' ? v : `#${v}`);
          }}
          placeholder={DEFAULT_CUT_COLOR}
          className={`h-10 w-28 font-mono ${!valid && hex !== '' ? 'border-destructive' : ''}`}
          aria-label={t('production.filmCutColor.hexAria', { location: location.name })}
          disabled={isSaving}
          maxLength={7}
        />
        {/* Line preview — colour paired with the hex text, never colour-only. */}
        <div
          aria-hidden
          className="h-2 w-24 rounded-full border"
          style={{ backgroundColor: valid ? hex : DEFAULT_CUT_COLOR }}
        />
        <Button
          size="sm"
          className="h-10"
          onClick={() => void onSave(location.id, hex)}
          disabled={isSaving || !valid || !dirty}
        >
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('production.filmCutColor.save')}
        </Button>
        {saved && (
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 text-muted-foreground hover:text-foreground"
            onClick={() => void onSave(location.id, null)}
            disabled={isSaving}
            aria-label={t('production.filmCutColor.clearAria', { location: location.name })}
            title={t('production.filmCutColor.clearTooltip')}
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

interface FilmCutColorSectionProps {
  /** Console single-location mode (see FieryHotFolderSection). */
  locationId?: string;
}

export function FilmCutColorSection({ locationId }: FilmCutColorSectionProps = {}) {
  const { t } = useTranslation('settings');
  const locationsQuery = trpc.productionSettings.getLocationSettings.useQuery();
  const utils = trpc.useUtils();

  const updateMutation = trpc.productionSettings.setLocationFilmCutColor.useMutation({
    onSuccess: (_data, variables) => {
      const loc = locationsQuery.data?.find((l) => l.id === variables.locationId);
      const locName = loc?.name ?? t('production.filmCutColor.locationFallback');
      toast.success(
        variables.color
          ? t('production.filmCutColor.toastSaved', { location: locName, color: variables.color })
          : t('production.filmCutColor.toastCleared', { location: locName }),
      );
      void utils.productionSettings.getLocationSettings.invalidate();
    },
    onError: (error) => {
      toast.error(t('production.filmCutColor.toastFailed', { message: error.message }));
    },
  });

  const pendingLocationId =
    updateMutation.isPending ? updateMutation.variables?.locationId ?? null : null;

  const productionLocations: LocationRow[] = (locationsQuery.data ?? [])
    .filter((l) => l.productionEnabled && (!locationId || l.id === locationId))
    .map((l) => ({
      id: l.id,
      name: l.name,
      filmCutColor: l.filmCutColor ?? null,
    }));

  const handleSave = (locId: string, color: string | null) =>
    updateMutation.mutateAsync({ locationId: locId, color }).then(() => undefined).catch(() => undefined);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <Scissors className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">
            {t('production.filmCutColor.description')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('production.filmCutColor.hint', { default: DEFAULT_CUT_COLOR })}
          </p>
        </div>
      </div>

      {locationsQuery.isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('production.filmCutColor.loadFailedTitle')}</AlertTitle>
          <AlertDescription className="flex items-center gap-2">
            {locationsQuery.error?.message ?? t('production.filmCutColor.loadFailedBody')}
            <Button
              variant="link"
              className="p-0 h-auto"
              onClick={() => void locationsQuery.refetch()}
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              {t('production.filmCutColor.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {locationsQuery.isLoading && (
        <div className="flex items-center justify-center py-6" role="status" aria-busy="true">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">
            {t('production.filmCutColor.loading')}
          </span>
        </div>
      )}

      {!locationsQuery.isLoading && !locationsQuery.isError && productionLocations.length === 0 && (
        <p className="text-sm text-muted-foreground py-4">
          {t('production.filmCutColor.noLocations')}
        </p>
      )}

      {!locationsQuery.isLoading && !locationsQuery.isError && productionLocations.length > 0 && (
        <div className="space-y-3">
          {!locationId && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
              <h4 className="font-medium text-base">{t('production.filmCutColor.locationsTitle')}</h4>
            </div>
          )}
          <div className="space-y-1" role="list" aria-label={t('production.filmCutColor.listAria')}>
            {productionLocations.map((location) => (
              <FilmCutColorRow
                key={location.id}
                location={location}
                onSave={handleSave}
                isSaving={pendingLocationId === location.id}
                showName={!locationId}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
